/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useMemo, useState } from "react";

import { Table, Select } from "antd";
import { Link } from "react-router-dom";
import { FaFirstAid } from "react-icons/fa";
import infoImage from "../../Images/info-icon.svg";

import GaugeChart from "./Gaugehart";
import "react-circular-progressbar/dist/styles.css";
import Popout from "../Common/Popout";
import "../../Styles/styles-ACWRTable.css";
import { get_colors } from "../../Colors/general";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector } from "react-redux";
import "react-circular-progressbar/dist/styles.css"
import Highcharts from 'highcharts';
import highchartsMore from "highcharts/highcharts-more";
import HighchartsReact from 'highcharts-react-official';
import { useDispatch } from "react-redux";
import {
  getPlayerGPSData,
  setPlayerFilter,
} from "../../store/actions/player.actions";
import { mapDataForRadialBarChart } from "../../mappers/model/RadialBarChart"
import create from "@ant-design/icons/lib/components/IconFont";
import { createGaugeOptions } from "../../mappers/model/GaugeMapper"

highchartsMore(Highcharts);



const formatTeamData = (res) => {
  if (!res || !res.team_data) {
    return {};
  }
  const { team_data } = res;

  let teamObjTemp = {};
  teamObjTemp.name = "Team";
  Object.keys(team_data).forEach((metric_name) => {
    teamObjTemp[`${metric_name}_ACWR`] = team_data[metric_name]?.["ACWR"];
    teamObjTemp[`${metric_name}_acute`] = team_data[metric_name]?.["acute"];
    teamObjTemp[`${metric_name}_acute_max`] =
      team_data[metric_name]?.["acute_max"];
    teamObjTemp[`${metric_name}_chronic`] = team_data[metric_name]?.["chronic"];
    teamObjTemp[`${metric_name}_chronic_max`] =
      team_data[metric_name]?.["chronic_max"];
  });

  return teamObjTemp;
};

export function ACWRTable(props) {
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(20);

  const {
    showTablePopout,
    setShowTablePopout,
    tablePopoutContent,
    displayTablePopout,
    ACWR_UPPER,
    ACWR_LOWER,
    team_name,
    position_filter,
  } = props;

  const handleRedirectToPlayer = (data) => async () => {
    if (data.player_id) {
      dispatch(setPlayerFilter({ player: data.player_id }));
      dispatch(getPlayerGPSData());
    }
  };

  let columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
      sorter: {
        compare: (a, b) => {
          if (a.name === "Team") return 0;
          if (b.name === "Team") return 0;
          else return a.name.localeCompare(b.name);
        },
        multiple: 1,
      },
      sortDirections: ["descend", "ascend"],
      render: (name, data) => {
        if (name === "Team") {
          return {
            props: {
              style: {
                height: "120px",
                paddingBottom: "10px",
                margin: "0",
                padding: "0",
              },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/teamdata",
                }}
              >
                {name}
              </Link>
            ),
          };
        } else if (data["color"] === "red") {
          return {
            props: {
              style: {
                width: "270px",
                height: "120px",
                paddingBottom: "10px",
                margin: "0",
                padding: "0",
              },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                }}
                onClick={handleRedirectToPlayer(data)}
              >
                {formattedPlayerName(team_name, name)}
                <FaFirstAid className="injury-icon" size={17} />
              </Link>
            ),
          };
        } else if (data["color"] === "yellow") {
          return {
            props: {
              style: {
                width: "270px",
                height: "120px",
                margin: "0",
                padding: "0",
                paddingBottom: "10px",
              },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                }}
                onClick={handleRedirectToPlayer(data)}
              >
                {formattedPlayerName(team_name, name)}
                <FaFirstAid className="injury-icon-yellow" size={17} />
              </Link>
            ),
          };
        } else {
          return {
            props: {
              style: {
                width: "100px",
                height: "80px",
                paddingBottom: "10px",
                margin: "0",
                padding: "0",
              },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                }}
                onClick={handleRedirectToPlayer(data)}
              >
                {formattedPlayerName(team_name, name)}
              </Link>
            ),
          };
        }
      },
    },
  ];

  for (let i = 0; i < props.data.metrics.length; i++) {
    let metric = props.data.metrics[i];
    let key = metric["key"] + "_ACWR";
    let chronicKey = metric["key"] + "_chronic";
    let chronicMax = metric["key"] + "_chronic_max";
    let acuteKey = metric["key"] + "_acute";
    let acuteMax = metric["key"] + "_acute_max";
    
    let name = metric["name"];
    columns.push({
      title: (
        <div className="tabel_header_section">
          <img
            src={infoImage}
            alt="inof-img"
            onClick={() => displayTablePopout(name)}
          />
          {name}
        </div>
      ),
      dataIndex: key,
      key: key,
      sortDirections: ["descend", "ascend"],
      width: "13.33%",
      sorter: {
        compare: (a, b) => {
          if (a.name === "Team" || b.name === "Team") return 0;

          return a[key] - b[key];
        },
        multiple: columns.length + 1,
      },
      render(value, res, index) {
        let chartOptions = mapDataForRadialBarChart(res, team_name, {
          chronicKey,
          chronicMax,
          acuteKey,
          acuteMax
        }, metric)
        return {
          props: {
            className:
              value > ACWR_UPPER
                ? "lowerClass"
                : value >= ACWR_LOWER && value <= ACWR_UPPER
                ? "middleClass"
                : "upperClass",
            style:
              res["name"] === "Team" && props.data?.team_data
                ? {}
                : {
                  },
          },
          children:
            res["name"] === "Team" && props.data?.team_data ? (
              <>
               <div className="chart-container">
                  <div className="chart-wrapper">
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={{...chartOptions, chart: {...chartOptions?.chart, height: '150px'}}}
                    />
                    <div className="inner-Legend-container">
                      <span className="inner--legend-label">{value}</span>
                      <span className="inner--legend-label">ACWR</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div
                style={{
                  background:
                    value > ACWR_UPPER
                      ? get_colors(team_name).upper
                      : value >= ACWR_LOWER && value <= ACWR_UPPER
                      ? get_colors(team_name).middle
                      : get_colors(team_name).lower,
                  height: "40px",
                  width: "100%",
                  padding: "0",
                  margin: "0",
                }}
              ></div>
            ),
        };
      },
    });
  }


  columns.push({
    title: (
      <div
        className="tabel_header_section"
        onClick={() => displayTablePopout("readiness_ACWR")}
      >
        <img src={infoImage} alt="inof-img" />
        Readiness
      </div>
    ),
    dataIndex: "readiness_ACWR",
    key: "readiness_ACWR",
    width: "13.33%",
    sorter: {
      compare: (a, b) => {
        if (a.name === "Team" || b.name === "Team") return 0;
        else return a.readiness_ACWR - b.readiness_ACWR;
      },
      multiple: columns.length + 1,
    },
    sortDirections: ["descend", "ascend"],
    render(readiness_in, res, index) {
      return {
        props: {
          className:
            readiness_in > ACWR_UPPER
              ? "lowerClass"
              : readiness_in >= ACWR_LOWER && readiness_in <= ACWR_UPPER
              ? "middleClass"
              : "upperClass",
          style:
            res["name"] === "Team" && props.data?.team_data
              ? {}
              : {
                  padding: "0",
                },
        },
        children:
          res["name"] !== "Team" ? (
            <div
              style={{
                background:
                  readiness_in > ACWR_UPPER
                    ? get_colors(team_name).upper
                    : readiness_in >= ACWR_LOWER && readiness_in <= ACWR_UPPER
                    ? get_colors(team_name).middle
                    : get_colors(team_name).lower,
                height: "40px",
                width: "100%",
                padding: "0",
                margin: "0",
              }}
            ></div>
          ) : (
            ""
          ),
      };
    },
  });

  columns.push({
    title: (
      <span className="tabel_header_section overall">
        <img
          src={infoImage}
          alt="inof-img"
          onClick={() => displayTablePopout("overall")}
        />
        Overall
      </span>
    ),
    dataIndex: "overall",
    key: "overall",
    width: "13.33%",
    sortDirections: ["descend", "ascend"],
    sorter: {
      compare: (a, b) => {
        if (a.name === "Team" || b.name === "Team") return 0;
        else return a.overall - b.overall;
      },
      multiple: columns.length + 1,
    },
    render(overall, res, index) {
      return {
        props: {
          className:
            overall > ACWR_UPPER
              ? "lowerClass"
              : overall >= ACWR_LOWER && overall <= ACWR_UPPER
              ? "middleClass"
              : "upperClass",
          style: {
            padding: "0",
            paddingRight: "15px",
          },
        },
        children:
          overall > ACWR_UPPER ? (
            <span
              role="img"
              style={{
                display: "table",
                background: get_colors(team_name).upper,
                fontSize: "100%",
                height: "40px",
                width: "100%",
                padding: "0",
                margin: "0",
              }}
              aria-label="overworked"
            ></span>
          ) : overall >= ACWR_LOWER && overall <= ACWR_UPPER ? (
            <span
              style={{
                display: "table",
                background: get_colors(team_name).middle,
                fontSize: "100%",
                height: "40px",
                width: "100%",
                padding: "0",
                margin: "0",
              }}
              role="img"
              aria-label="good"
            ></span>
          ) : res["name"] !== "Team" ? (
            <span
              role="img"
              style={{
                display: "table",
                background:
                  overall > ACWR_UPPER
                    ? get_colors(team_name).upper
                    : overall >= ACWR_LOWER && overall <= ACWR_UPPER
                    ? get_colors(team_name).middle
                    : get_colors(team_name).lower,
                height: "40px",
                width: "100%",
                padding: "0",
                margin: "0",
              }}
              aria-label="underworked"
            ></span>
          ) : (
            ""
          ),
      };
    },
  });

  let filteredRowData = props.data.data;
  if (position_filter) {
    filteredRowData = filteredRowData.filter(
      (x) => x.position === position_filter
    );
  }

  const dataSource = useMemo(
    () =>
      props.data?.team_data
        ? [{ ...formatTeamData(props.data) }, ...filteredRowData]
        : filteredRowData,
    [props.data, filteredRowData]
  );

  return (
    <div style={{ position: "relative" }}>
      <Popout
        showPopout={showTablePopout}
        setShowPopout={setShowTablePopout}
        popOutStyles={{ paddingTop: "12%", paddingLeft: "30%" }}
      >
        {tablePopoutContent}
      </Popout>
      <div style={{ width: "100%", marginTop: "15px" }}>
        {props.showWeekly ? (
          <WeeklyTable
            {...props}
            showTablePopout={showTablePopout}
            setShowTablePopout={setShowTablePopout}
            tablePopoutContent={tablePopoutContent}
            team_name={team_name}
          />
        ) : (
          <>
            <Table
              style={{
                marginTop: props.data?.data?.length <= 0 ? "73px" : "unset",
                width: "100%",
              }}
              rowKey="rowId"
              columns={columns}
              size="middle"
              className="acwr-table"
              dataSource={dataSource}
              pagination={{
                pageSize: pageSize,
                simple: true,
                style: { zIndex: 13, position: "relative" },
                position: ["topLeft"],
              }}
              loading={props.loading}
            />
            <Select
              value={pageSize}
              onChange={setPageSize}
              style={{ position: "absolute", top: 12, left: 172, zIndex: 14 }}
            >
              <Select.Option value={20}>20 / page</Select.Option>
              <Select.Option value={30}>30 / page</Select.Option>
              <Select.Option value={dataSource.length}>Show All</Select.Option>
            </Select>
          </>
        )}
      </div>
    </div>
  );
}

function WeeklyTable(props) {
  const dispatch = useDispatch();

  const handleRedirectToPlayer = (data) => () => {
    if (data.player_id) {
      dispatch(setPlayerFilter(data.player_id));
    }
  };
  const { displayTablePopout } = props;
  let columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      showSorterTooltip: false,
      sortDirections: ["descend", "ascend"],
      sorter: {
        compare: (a, b) => {
          if (a.name === "Team") return 0;
          if (b.name === "Team") return 0;
          else return a.name.localeCompare(b.name);
        },
        multiple: 1,
      },
      render: (name, data) => {
        if (name === "Team") {
          return {
            props: {
              style: { width: "270px", margin: "0", padding: "0px" },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/teamdata",
                }}
              >
                {name}
              </Link>
            ),
          };
        } else if (data["color"] === "yellow") {
          return {
            props: {
              style: { width: "270px", margin: "0", padding: "0px" },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                }}
                onClick={handleRedirectToPlayer(data)}
              >
                {formattedPlayerName(props.team_name, name)}
                <FaFirstAid className="injury-icon-yellow" size={17} />
              </Link>
            ),
          };
        } else if (data["color"] === "red") {
          return {
            props: {
              style: { width: "270px", margin: "0", padding: "0px" },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                }}
                onClick={handleRedirectToPlayer(data)}
              >
                {formattedPlayerName(props.team_name, name)}
                <FaFirstAid className="injury-icon" size={17} />
              </Link>
            ),
          };
        } else if (data["color"] === "yellow") {
          return {
            props: {
              style: { width: "270px", margin: "0", padding: "0px" },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                  state: { name: name },
                }}
              >
                {formattedPlayerName(props.team_name, name)}
                <FaFirstAid className="injury-icon-yellow" size={17} />
              </Link>
            ),
          };
        } else {
          return {
            props: {
              style: {
                width: "270px",
                margin: "0",
                padding: "0px",
              },
            },
            children: (
              <Link
                style={{ color: "black" }}
                className="nav-link"
                to={{
                  pathname: "/playerdata",
                  state: { name: name },
                }}
              >
                {" "}
                {formattedPlayerName(props.team_name, name)}
              </Link>
            ),
          };
        }
      },
    },
  ];

  for (let i = 0; i < props.data.metrics.length; i++) {
    let metric = props.data.metrics[i];
    let key = metric["key"];
    let metricKey = key;
    let name = metric["name"];
    columns.push({
      title: (
        <span className="tabel_header_section">
          <img
            src={infoImage}
            alt="inof-img"
            onClick={() => displayTablePopout(name)}
          />
          {name}
        </span>
      ),
      dataIndex: key,
      key: key,
      sortDirections: ["descend", "ascend"],
      // sortDirections: ["descend"],
      align: 'center',
      showSorterTooltip: false,
      sorter: {
        compare: (a, b) => {
          if (a.name === "Team") return 0;
          if (b.name === "Team") return 0;
          return a[key] - b[key];
        },
        multiple: columns.length + 1,
      },
      render(key, rest) {
        const max = rest[metricKey + "_max"]
        const options = createGaugeOptions(key, max)
        return {
          // <GaugeChart value={key} max={rest[metricKey+'_max']}/>
          children:    
          <div style={{
            width: '9rem',
            margin: '0 auto' 
          }}>
            <HighchartsReact
              highcharts={Highcharts}
              options={{...options, chart: {...options?.chart, height: '75%'}}}
            />
          </div>
        };
      },
    });
  }

  columns.push({
    title: (
      <span className="tabel_header_section">
        <img
          src={infoImage}
          alt="inof-img"
          onClick={() => displayTablePopout("overall")}
        />
        Overall
      </span>
    ),
    dataIndex: "overall",
    key: "overall",
    sortDirections: ["descend", "ascend"],
    showSorterTooltip: false,
    sorter: {
      compare: (a, b) => {
        console.log(a, b);
        if (a.name === "Team") return 0;
        if (b.name === "Team") return 0;
        return a.overall - b.overall;
      },
      multiple: columns.length + 1,
    },
    render(overall, { overall_max }) {
      const options = createGaugeOptions(overall, overall_max)
      return {        
        children:    
          <div style={{width:'9rem'}}>
            <HighchartsReact
              highcharts={Highcharts}
              options={{...options, chart: {...options?.chart, height: '75%'}}}
            />
          </div>
      };
    },
  });

  let filteredRowData = props.data.data;
  if (props.position_filter) {
    filteredRowData = filteredRowData.filter(
      (x) => x.position === props.position_filter || x.name == "Team"
    );
  }

  return (
    <Table
      style={{
        marginTop: props.data?.data?.length <= 0 ? "73px" : "unset",
        width: "100%",
      }}
      rowKey="rowId"
      columns={columns}
      size="middle"
      dataSource={filteredRowData}
      className="acwr-table"
      pagination={{
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30"],
        position: ["topLeft"],
        simple: true,
      }}
      loading={props.loading}
    />
  );
}
